import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import DeletePartModal from '../components/DeletePartModal.jsx'
import MergePartsPanel from '../components/MergePartsPanel.jsx'
import PageHeader from '../components/PageHeader.jsx'
import QuickStockPanel from '../components/QuickStockPanel.jsx'
import SpecValue from '../components/SpecValue.jsx'
import StatusBadge from '../components/StatusBadge.jsx'
import { useAuth } from '../components/AuthProvider.jsx'
import { useInventory } from '../components/InventoryProvider.jsx'
import { useToast } from '../components/ToastProvider.jsx'
import { modelMatchLabel, usageStatusLabel } from '../data/constants'
import { ProjectBadges } from '../components/ProjectPicker.jsx'
import { canAdjustStock, canManageParts, canTransact } from '../utils/permissions'
import { formatDate, formatDateTime } from '../utils/format'
import { formatCurrentStock, formatSignedQty, isMergedPart, needsReview, partTitle, signedTransactionQty } from '../utils/stock'

export default function PartDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const manageParts = canManageParts(user)
  const allowTransact = canTransact(user)
  const allowAdjust = canAdjustStock(user)
  const {
    getPart,
    loadPart,
    fetchPartHistory,
    stockIn,
    stockOut,
    stockAdjust,
    mergePart,
    deletePart,
    loading,
  } = useInventory()
  const { show } = useToast()
  const cached = getPart(id)
  const [fetched, setFetched] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [partTx, setPartTx] = useState([])
  const [panel, setPanel] = useState({ open: false, type: 'out', qty: 1 })
  const [mergeOpen, setMergeOpen] = useState(false)
  const [merging, setMerging] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const part = cached || fetched

  useEffect(() => {
    let cancelled = false
    if (cached) {
      setFetched(null)
      setLoadError('')
      return undefined
    }
    loadPart(id)
      .then((loaded) => {
        if (cancelled) return
        if (loaded) setFetched(loaded)
        else setLoadError('부품을 찾을 수 없습니다.')
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || '데이터를 불러오는 중 오류가 발생했습니다.')
      })
    return () => {
      cancelled = true
    }
  }, [id, cached, loadPart])

  useEffect(() => {
    let cancelled = false
    fetchPartHistory(id)
      .then((rows) => {
        if (!cancelled) setPartTx(rows)
      })
      .catch(() => {
        if (!cancelled) setPartTx([])
      })
    return () => {
      cancelled = true
    }
  }, [id, part?.quantity, fetchPartHistory])

  if (!part) {
    return (
      <div className="page">
        <PageHeader title={loadError || (loading ? '불러오는 중...' : '부품을 찾을 수 없습니다.')} />
        <Link to="/inventory" className="btn btn--primary btn--touch">
          재고 목록으로
        </Link>
      </div>
    )
  }

  function openPanel(type, qty = 1) {
    setPanel({ open: true, type, qty })
  }

  async function handleSubmit({ type, quantity, targetQuantity, memo }) {
    if (type === 'in') {
      await stockIn({ partId: part.id, quantity, memo })
      show(`입고 ${quantity} ${part.unit} 처리했습니다.`)
    } else if (type === 'out') {
      await stockOut({ partId: part.id, quantity, memo })
      show(`출고 ${quantity} ${part.unit} 처리했습니다.`)
    } else {
      await stockAdjust({ partId: part.id, targetQuantity, memo })
      show(`재고를 ${targetQuantity} ${part.unit}(으)로 조정했습니다.`)
    }
    setPanel({ open: false, type, qty: 1 })
  }

  const extraSpecs = (part.additionalSpecs || []).filter((spec) => spec.key || spec.value)
  const merged = isMergedPart(part)
  const canAct = !merged

  async function handleMerge(target) {
    setMerging(true)
    try {
      const result = await mergePart(part.id, target.id)
      show(`${partTitle(part)}을 ${partTitle(result.target)}에 병합했습니다.`)
      setMergeOpen(false)
      navigate(`/inventory/${result.target.id}`)
    } finally {
      setMerging(false)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    try {
      await deletePart(part.id)
      show('품목이 삭제되었습니다.')
      setDeleteOpen(false)
      navigate('/inventory')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="page page--detail">
      <PageHeader
        title={<span className="model-name">{partTitle(part)}</span>}
        description={`${part.partCode} · ${part.manufacturer} · ${part.name}`}
        actions={
          <>
            <Link to="/inventory" className="btn btn--ghost btn--touch">
              목록
            </Link>
            {manageParts && canAct ? (
              <button type="button" className="btn btn--ghost btn--touch" onClick={() => navigate(`/inventory/${part.id}/edit`)}>
                정보 수정
              </button>
            ) : null}
            {needsReview(part) && manageParts && canAct ? (
              <Link to={`/inventory/review?part=${part.id}`} className="btn btn--primary btn--touch">
                이 품목 검수
              </Link>
            ) : null}
            {manageParts && canAct ? (
              <button type="button" className="btn btn--danger btn--touch" onClick={() => setDeleteOpen(true)}>
                품목 삭제
              </button>
            ) : null}
          </>
        }
      />

      {merged ? (
        <p className="merge-banner">
          이 품목은{' '}
          <Link to={`/inventory/${part.mergedIntoPartId}`}>{part.mergedIntoPartCode || '대표 품목'}</Link>
          로 병합되었습니다.
        </p>
      ) : null}

      <section className="stock-hero">
        <p>현재재고</p>
        <div className="stock-hero__qty">
          <strong>{part.quantityNeedsVerification ? '미확인' : part.quantity}</strong>
          {part.quantityNeedsVerification ? null : <span>{part.unit}</span>}
        </div>
        <div className="stock-hero__meta">
          <StatusBadge part={part} />
          <StatusBadge kind="verification" part={part} />
          <span>
            최소재고 {part.minStock ?? part.minimumStock} {part.unit}
          </span>
        </div>
        {part.quantityNeedsVerification ? (
          <p className="muted">초기 조사 수량을 현물과 대조해 확인해 주세요. 수량 변경은 재고조정을 사용합니다.</p>
        ) : null}
      </section>

      {canAct && (allowTransact || allowAdjust) ? (
        <div className="detail-cta">
          {allowTransact ? (
            <button type="button" className="btn btn--primary btn--xl" onClick={() => openPanel('in', 1)}>
              입고
            </button>
          ) : null}
          {allowTransact ? (
            <button type="button" className="btn btn--danger btn--xl" onClick={() => openPanel('out', 1)}>
              출고
            </button>
          ) : null}
          {allowAdjust ? (
            <button type="button" className="btn btn--ghost btn--xl" onClick={() => openPanel('adjust', 1)}>
              재고조정
            </button>
          ) : null}
        </div>
      ) : null}

      {manageParts && canAct ? (
        <section className="panel merge-admin">
          <h2>관리 기능</h2>
          <p className="muted">
            전수조사 중 같은 부품이 두 번 등록된 경우 남길 대표 품목으로 합칩니다. 재고와 거래이력이 없는 오등록
            품목은 바로 삭제할 수 있습니다.
          </p>
          <div className="merge-admin__actions">
            <button type="button" className="btn btn--ghost btn--touch" onClick={() => setMergeOpen(true)}>
              중복 품목 병합
            </button>
            <button type="button" className="btn btn--danger btn--touch" onClick={() => setDeleteOpen(true)}>
              품목 삭제
            </button>
          </div>
        </section>
      ) : null}

      <section className="panel spec-panel">
        <h2>부품 정보</h2>
        <dl className="spec-grid">
          <div>
            <dt>관리번호</dt>
            <dd>{part.partCode}</dd>
          </div>
          <div>
            <dt>분류</dt>
            <dd>{part.category || '-'}</dd>
          </div>
          <div>
            <dt>제조사</dt>
            <dd>{part.manufacturer}</dd>
          </div>
          <div>
            <dt>모델</dt>
            <dd className="model-name">{part.model || '-'}</dd>
          </div>
          <div>
            <dt>품명</dt>
            <dd>{part.name || '-'}</dd>
          </div>
          <div className="spec-wide">
            <dt>사용 프로젝트</dt>
            <dd>
              <ProjectBadges projects={part.projects} />
            </dd>
          </div>
          <div>
            <dt>모델 일치</dt>
            <dd>{modelMatchLabel(part.modelMatchStatus)}</dd>
          </div>
          <div>
            <dt>크기</dt>
            <dd>{part.size || '-'}</dd>
          </div>
          <div>
            <dt>보관방식</dt>
            <dd>{part.storageMethod || '-'}</dd>
          </div>
          <div>
            <dt>위치</dt>
            <dd>{part.location || '-'}</dd>
          </div>
          <div>
            <dt>현재재고</dt>
            <dd className="td-qty">{formatCurrentStock(part)}</dd>
          </div>
          <div>
            <dt>단위</dt>
            <dd>{part.unit}</dd>
          </div>
          <div>
            <dt>최소재고</dt>
            <dd>
              {part.minStock ?? part.minimumStock} {part.unit}
            </dd>
          </div>
          <div>
            <dt>정격전압</dt>
            <dd>
              <SpecValue value={part.ratedVoltage} />
            </dd>
          </div>
          <div>
            <dt>정격전류</dt>
            <dd>
              <SpecValue value={part.ratedCurrent} />
            </dd>
          </div>
          <div>
            <dt>극수</dt>
            <dd>
              <SpecValue value={part.poles} />
            </dd>
          </div>
          <div>
            <dt>차단용량</dt>
            <dd>
              <SpecValue value={part.breakingCapacity} />
            </dd>
          </div>
          <div>
            <dt>전력</dt>
            <dd>
              <SpecValue value={part.power} />
            </dd>
          </div>
          <div>
            <dt>접점구성</dt>
            <dd>
              <SpecValue value={part.contactConfiguration} />
            </dd>
          </div>
          <div>
            <dt>코일전압</dt>
            <dd>
              <SpecValue value={part.coilVoltage} />
            </dd>
          </div>
          <div>
            <dt>통신방식</dt>
            <dd>
              <SpecValue value={part.communication} />
            </dd>
          </div>
          <div>
            <dt>치수</dt>
            <dd>
              <SpecValue value={part.dimensions} />
            </dd>
          </div>
          <div>
            <dt>AC / DC</dt>
            <dd>
              <SpecValue value={part.powerType} />
            </dd>
          </div>
          <div>
            <dt>상태</dt>
            <dd>{usageStatusLabel(part.status)}</dd>
          </div>
          <div>
            <dt>데이터 확인 상태</dt>
            <dd>
              <StatusBadge kind="verification" part={part} />
            </dd>
          </div>
          {part.source ? (
            <div>
              <dt>출처</dt>
              <dd>{part.source}</dd>
            </div>
          ) : null}
          <div className="spec-wide">
            <dt>설명</dt>
            <dd>{part.description || part.specs || '-'}</dd>
          </div>
          <div className="spec-wide">
            <dt>조사 메모</dt>
            <dd>{part.inventoryNote || '-'}</dd>
          </div>
          <div className="spec-wide">
            <dt>비고</dt>
            <dd>{part.note || part.memo || '-'}</dd>
          </div>
        </dl>
      </section>

      <section className="panel spec-panel">
        <h2>추가 사양</h2>
        {extraSpecs.length === 0 ? (
          <p className="empty">등록된 추가 사양이 없습니다.</p>
        ) : (
          <dl className="spec-grid">
            {extraSpecs.map((spec, index) => (
              <div key={`${spec.key}-${index}`}>
                <dt>{spec.key || '추가 사양'}</dt>
                <dd>
                  <SpecValue value={spec.value} />
                </dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      <section className="panel">
        <div className="panel__head">
          <h2>재고 변동 이력</h2>
          <Link to="/transactions">전체 이력</Link>
        </div>
        {partTx.length === 0 ? (
          <p className="empty">이 부품의 재고 이력이 없습니다.</p>
        ) : (
          <ul className="plain-list">
            {partTx.map((tx) => {
              const delta = signedTransactionQty(tx)
              const remain = tx.afterQuantity ?? tx.balanceAfter
              return (
                <li key={tx.id} className="plain-row plain-row--static">
                  <div>
                    <strong>{formatDate(tx.createdAt)}</strong>
                    <span>
                      {formatDateTime(tx.createdAt)} · {tx.memo || tx.worker || ''}
                    </span>
                  </div>
                  <div className="plain-row__meta">
                    <b className={delta < 0 ? 'tx-delta--minus' : 'tx-delta--plus'}>
                      {formatSignedQty(delta)}
                    </b>
                    <span className="muted">잔여 {remain}</span>
                    <StatusBadge kind="tx" status={tx.type} />
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <MergePartsPanel
        source={part}
        open={mergeOpen && manageParts && canAct}
        saving={merging}
        onClose={() => setMergeOpen(false)}
        onConfirm={handleMerge}
      />

      <DeletePartModal
        part={part}
        open={deleteOpen && manageParts && canAct}
        saving={deleting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />

      <QuickStockPanel
        open={
          panel.open &&
          ((panel.type === 'adjust' && allowAdjust) || (panel.type !== 'adjust' && allowTransact))
        }
        type={panel.type}
        part={part}
        initialQty={panel.qty}
        onClose={() => setPanel((current) => ({ ...current, open: false }))}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
